import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Copy, Gift, Sparkles, Users, Calendar, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import ShareOnWhatsApp from "@/components/shared/ShareOnWhatsApp";

export default function ReferralPanel({ role }) {
  const [stats, setStats] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get("/referral/stats").then((r) => setStats(r.data)).catch((e) => {
      // silent — admin doesn't have referrals
      if (e.response?.status !== 403) toast.error(formatApiError(e.response?.data?.detail));
    });
  }, []);

  if (!stats) return null;

  const shareUrl = `${window.location.origin}/register/${role === "company" ? "company" : "staff"}?ref=${stats.referral_code}`;

  const waMessage = role === "company"
    ? `🛡️ SecuSource — a workforce platform built by the security industry, for the industry.

If your company hires security staff, register free and search vetted pros/teams in Ireland & UK.

✅ Free to register
✅ One transparent fee (€1/hr per worker OR 3% — whichever is lower)
✅ PSA / SIA verified pros, CPOs, cluster teams
✅ Contact details anonymous until booking

Use my referral link so we both benefit:
${shareUrl}`
    : `🛡️ SecuSource — a workforce platform built by security & CPOs, for the industry.

If you're a licensed security pro, CPO, driver, supervisor or medic — register free and get discovered by companies in Ireland & UK.

✅ €0 to join · No membership fee
✅ You set your own rate
✅ Anonymous profile until you accept a booking
✅ Lead or join a cluster team

Use my referral link (I get a search-ranking boost, you get fast-tracked):
${shareUrl}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Referral link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy — link: " + shareUrl);
    }
  };

  const boostEnd = stats.priority_boost_until
    ? new Date(stats.priority_boost_until).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
    : null;

  return (
    <div className="card-flat p-6 border-l-4 border-l-brand" data-testid="referral-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-brand font-outfit font-bold mb-1 flex items-center gap-2">
            <Gift className="w-3.5 h-3.5" /> Referral
          </div>
          <h2 className="text-xl font-outfit font-semibold text-white">Refer a {role === "company" ? "company" : "pro"}</h2>
          <p className="text-sm text-white/70 font-manrope mt-1 max-w-xl">
            {role === "company"
              ? `Earn €${stats.company_credit_per_referral} fee credit per approval.`
              : `Boost your search rank for ${stats.pro_priority_boost_days} days per verified referral.`}
          </p>
        </div>
        <div className="flex gap-3 flex-wrap">
          <div className="bg-navy-900 text-white px-4 py-2 rounded-md font-mono font-bold text-lg tracking-widest" data-testid="referral-code-display">
            {stats.referral_code}
          </div>
          <Button onClick={copy} className="bg-brand hover:bg-brand-hover text-white" data-testid="referral-copy-btn">
            <Copy className="w-4 h-4 mr-2" /> {copied ? "Copied!" : "Copy Link"}
          </Button>
          <ShareOnWhatsApp message={waMessage} testId="referral-share-whatsapp" label="WhatsApp" />
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5">
        <div className="bg-[#0E0E0E] rounded-md p-3">
          <div className="text-xs uppercase text-white/60 font-outfit tracking-widest flex items-center gap-1"><Users className="w-3 h-3" /> Approved</div>
          <div className="text-2xl font-outfit font-bold text-white">{stats.referrals_approved}</div>
        </div>
        <div className="bg-[#0E0E0E] rounded-md p-3">
          <div className="text-xs uppercase text-white/60 font-outfit tracking-widest flex items-center gap-1"><Calendar className="w-3 h-3" /> Pending</div>
          <div className="text-2xl font-outfit font-bold text-white">{stats.referrals_pending}</div>
        </div>
        {role === "company" ? (
          <div className="bg-gold-50 rounded-md p-3 border border-gold-200 col-span-2">
            <div className="text-xs uppercase text-gold-600 font-outfit tracking-widest flex items-center gap-1"><Sparkles className="w-3 h-3" /> Fee credit available</div>
            <div className="text-2xl font-outfit font-bold text-gold-600">€{stats.fee_credit_remaining.toFixed(2)}</div>
          </div>
        ) : (
          <div className={`rounded-md p-3 col-span-2 border ${stats.priority_boost_active ? "bg-gold-50 border-gold-200" : "bg-[#0E0E0E] border-white/10"}`}>
            <div className={`text-xs uppercase font-outfit tracking-widest flex items-center gap-1 ${stats.priority_boost_active ? "text-gold-600" : "text-white/60"}`}>
              {stats.priority_boost_active ? <CheckCircle2 className="w-3 h-3" /> : <Sparkles className="w-3 h-3" />}
              Priority Boost
            </div>
            <div className={`text-lg font-outfit font-bold ${stats.priority_boost_active ? "text-gold-600" : "text-white/60"}`}>
              {stats.priority_boost_active ? `Active until ${boostEnd}` : "Not active"}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
